import { useState, useRef, createRef, useEffect } from "react";
import { ProgressBar, Tooltip, OverlayTrigger, Overlay } from "react-bootstrap";
import moment from "moment";

const ProgressReport = (props) => {
    const currentStep = props.step;
    const steps = props.legends || [];

    // ------------------------------
    // CREATE REFS DYNAMICALLY (WORKS AFTER REFRESH)
    // ------------------------------
    const moreRefs = useRef([]);

    useEffect(() => {
        if (steps.length > 0) {
            moreRefs.current = steps.map(() => createRef());
        }
    }, [steps]);

    // ------------------------------
    // TOOLTIP VISIBILITY STATE
    // ------------------------------
    const [showMore, setShowMore] = useState([]);

    useEffect(() => {
        if (steps.length > 0) {
            setShowMore(steps.map(() => false));
        }
    }, [steps]);

    // ------------------------------
    // TOGGLE CLICK TOOLTIP
    // ------------------------------
    const toggleTooltip = (index) => {
        setShowMore((prev) =>
            prev.map((v, i) => (i === index ? !v : false))
        );
    };

    // ------------------------------
    // CLOSE TOOLTIP ON OUTSIDE CLICK
    // ------------------------------
    useEffect(() => {
        const handleClickOutside = (event) => {
            let clickedInside = false;

            moreRefs.current.forEach((ref) => {
                if (ref?.current && ref.current.contains(event.target)) {
                    clickedInside = true;
                }
            });

            if (!clickedInside) {
                setShowMore(steps.map(() => false));
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [steps]);

    // ------------------------------
    // PROGRESS CALCULATION
    // ------------------------------
    const getProgress = (step) => {
        if (!steps.length) return 0;

        let baseProgress = (step / (steps.length - 1)) * 100;
        let adjustmentMap = {};

        if (steps.length === 2) adjustmentMap = { 0: 25, 1: 0 };
        else if (steps.length === 3) adjustmentMap = { 0: 18, 1: 0, 2: 5 };
        else if (steps.length === 4) adjustmentMap = { 0: 15, 1: 5, 2: -5, 3: 0 };
        else if (steps.length === 5) adjustmentMap = { 0: 10, 1: 5, 2: 0, 3: -5 };

        return Math.min(Math.max(baseProgress + (adjustmentMap[step] || 0), 0), 100);
    };

    // ------------------------------
    // TOOLTIP CONTENT
    // ------------------------------
    const renderTooltipContent = (step) => (
        <>
            {step.approval_list && step.approval_list.length > 0 ? (
                step.approval_list.map((item, jdex) => (
                    <div key={jdex} className="tool-view">
                        <p>{item.project_id || "N/A"}</p>
                        <p>{item.user_name || "N/A"}</p>
                        {item.approved_on && (<p>{moment(item.approved_on).format("DD MMM YY")}</p>)}
                    </div>
                ))
            ) : (
                <div>No approvals yet</div>
            )}
        </>
    );

    // ------------------------------
    // RENDER UI
    // ------------------------------
    return (
        <div className="progress-report-container">
            <div className="progress-report">
                <div className="d-flex justify-content-between align-items-center position-relative mb-3">

                    {steps.map((step, index) => (
                        <div key={index} className="text-center flex-fill position-relative">

                            {/* Hover Tooltip on Circle Icon */}

                            <div
                                className={`rounded-circle d-inline-flex align-items-center justify-content-center
                                        ${index <= currentStep ? "bg-warning text-white" : "bg-light text-dark"}`}
                                style={{
                                    width: "45px",
                                    height: "45px",
                                    border: "2px solid",
                                    cursor: "pointer",
                                    borderColor: index <= currentStep ? "orange" : "gray",
                                    fontSize: "20px",
                                }}
                            >
                                <i className={step.icon}></i>
                            </div>


                            {/* Step Name + More Button */}
                            <div className="progress-text mt-2">
                                <p><strong>{step.title}</strong></p>

                                <button
                                    ref={moreRefs.current[index]}
                                    onClick={() => toggleTooltip(index)}
                                    className="btn btn-link p-0"
                                    disabled={index === 0}
                                    style={{ color: index === 0 ? 'transparent' : '#777777' }}
                                >
                                    More..
                                </button>

                                {/* Click Tooltip */}
                                <Overlay
                                    target={moreRefs.current[index]?.current}
                                    show={showMore[index]}
                                    placement="right"
                                >
                                    <Tooltip className="custom-white-tooltip">
                                        {renderTooltipContent(step)}
                                    </Tooltip>
                                </Overlay>

                                {/* Approved Date */}
                                {step.approved_on ? (
                                    <p>{moment(step.approved_on).format("DD MMM YY")}</p>
                                ) : (
                                    <p className="null-text">null</p>
                                )}
                            </div>

                            {/* Connecting Line */}
                            {index < steps.length - 1 && (
                                <div
                                    className="position-absolute top-50 start-100 translate-middle w-100"
                                    style={{
                                        height: "4px",
                                        backgroundColor: index < currentStep ? "orange" : "gray",
                                        zIndex: -1,
                                    }}
                                />
                            )}
                        </div>
                    ))}

                </div>
            </div>

            {/* Bottom Progress Bar */}
            <ProgressBar
                now={getProgress(currentStep)}
                variant="warning"
                className="mt-3"
                style={{ height: "8px" }}
            />
        </div>
    );
};

export default ProgressReport;
